import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  InventorySupplierFilterParams,
  InventorySupplierRecord,
  InventorySupplierSummaryCounts,
  SaveInventorySupplierRequest,
} from '../types/inventory';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

const toNumber = (value: unknown) => {
  if (typeof value === 'number') {
    return value;
  }

  return Number(value || 0);
};

const mapInventorySupplier = (
  value: Record<string, unknown>,
): InventorySupplierRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  name: String(value.name || ''),
  contact_person: (value.contact_person as string | null) ?? null,
  phone: (value.phone as string | null) ?? null,
  email: (value.email as string | null) ?? null,
  tax_id: (value.tax_id as string | null) ?? null,
  address: (value.address as string | null) ?? null,
  payment_terms_days: toNumber(value.payment_terms_days),
  opening_balance: toNumber(value.opening_balance),
  is_active: Boolean(value.is_active),
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

const applyInventorySupplierFilters = <T>(
  query: T,
  filters: {
    search?: string;
    status?: InventorySupplierFilterParams['status'];
  },
) => {
  let nextQuery = query as any;
  const trimmedSearch = filters.search?.trim();

  if (filters.status && filters.status !== 'all') {
    nextQuery = nextQuery.eq('is_active', filters.status === 'active');
  }

  if (trimmedSearch) {
    nextQuery = nextQuery.or(
      `name.ilike.%${trimmedSearch}%,contact_person.ilike.%${trimmedSearch}%,phone.ilike.%${trimmedSearch}%,email.ilike.%${trimmedSearch}%,tax_id.ilike.%${trimmedSearch}%`,
    );
  }

  return nextQuery as T;
};

export const fetchInventorySuppliers = async ({
  companyId,
  search,
  status = 'all',
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: InventorySupplierFilterParams): Promise<
  ApiResponse<PaginatedResponse<InventorySupplierRecord>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('inventory_suppliers')
      .select('*', { count: 'exact' })
      .eq('company_id', companyId)
      .order('name', { ascending: true })
      .range(from, to);

    query = applyInventorySupplierFilters(query, { search, status });

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: (data || []).map((item) =>
          mapInventorySupplier(item as Record<string, unknown>),
        ),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory suppliers',
      data: undefined,
    };
  }
};

export const fetchInventorySupplierSummary = async (
  companyId: number,
  search?: string,
  status: InventorySupplierFilterParams['status'] = 'all',
): Promise<ApiResponse<InventorySupplierSummaryCounts>> => {
  try {
    const baseQuery = (activeStatus?: boolean) => {
      let query = supabase
        .from('inventory_suppliers')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId);

      query = applyInventorySupplierFilters(query, {
        search,
        status:
          activeStatus === undefined
            ? status
            : activeStatus
              ? 'active'
              : 'inactive',
      });

      return query;
    };

    const [totalResult, activeResult, inactiveResult, openingBalanceResult] =
      await Promise.all([
        baseQuery(),
        baseQuery(true),
        baseQuery(false),
        applyInventorySupplierFilters(
          supabase
            .from('inventory_suppliers')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', companyId)
            .gt('opening_balance', 0),
          { search, status },
        ),
      ]);

    const errors = [
      totalResult.error,
      activeResult.error,
      inactiveResult.error,
      openingBalanceResult.error,
    ].filter(Boolean);

    if (errors.length) {
      return { success: false, error: errors[0]?.message, data: undefined };
    }

    return {
      success: true,
      data: {
        totalSuppliers: totalResult.count || 0,
        activeSuppliers: activeResult.count || 0,
        inactiveSuppliers: inactiveResult.count || 0,
        suppliersWithOpeningBalance: openingBalanceResult.count || 0,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory supplier summary',
      data: undefined,
    };
  }
};

export const saveInventorySupplier = async (
  input: SaveInventorySupplierRequest,
): Promise<ApiResponse<InventorySupplierRecord>> => {
  const payload = {
    company_id: input.companyId,
    name: input.name.trim(),
    contact_person: input.contactPerson?.trim() || null,
    phone: input.phone?.trim() || null,
    email: input.email?.trim() || null,
    tax_id: input.taxId?.trim() || null,
    address: input.address?.trim() || null,
    payment_terms_days: input.paymentTermsDays ?? 0,
    opening_balance: input.openingBalance ?? 0,
    is_active: input.isActive ?? true,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('inventory_suppliers')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapInventorySupplier(data) };
    }

    const { data, error } = await supabase
      .from('inventory_suppliers')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapInventorySupplier(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save inventory supplier',
      data: undefined,
    };
  }
};

export const deleteInventorySupplier = async (
  companyId: number,
  supplierId: string,
): Promise<ApiResponse<null>> => {
  try {
    const { error } = await supabase
      .from('inventory_suppliers')
      .delete()
      .eq('company_id', companyId)
      .eq('id', supplierId);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to delete inventory supplier',
      data: null,
    };
  }
};
